import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingStreakView } from '@euler/model/coaching-progress';
import { Flame, Pencil, Trophy } from 'lucide-react-native';
import { memo } from 'react';
import { Pressable, View } from 'react-native';
import { aiColors, tint } from '../../../ai-suite/theme';

/**
 * 连续练习 n 天 and 本周 x / y 次. Compact on the start page; in 我的练习 it
 * also edits the goal and opens the store leaderboard.
 */
export const StreakCard = memo(
  ({
    streak,
    onEditGoal,
    onOpenLeaderboard,
  }: {
    streak: CoachingStreakView;
    onEditGoal?: () => void;
    onOpenLeaderboard?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const progress = Math.min(1, streak.weekPractices / streak.weeklyGoal);
    const reached = streak.weekPractices >= streak.weeklyGoal;
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(14),
          padding: rpx(14),
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
        }}
      >
        <View
          style={{
            width: rpx(40),
            height: rpx(40),
            borderRadius: rpx(20),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tint(aiColors.amber, streak.streak ? 0.15 : 0.06),
          }}
        >
          <Flame
            size={rpx(22)}
            color={streak.streak ? aiColors.amber : aiColors.mutedForeground}
          />
        </View>
        <View style={{ flex: 1, gap: rpx(6) }}>
          <Typography.Text size={rpx(15)} bold color={aiColors.foreground}>
            {streak.streak
              ? `连续练习 ${streak.streak} 天`
              : '今天练一次，开始连续练习'}
          </Typography.Text>
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            <View
              style={{
                flex: 1,
                height: rpx(5),
                borderRadius: rpx(3),
                overflow: 'hidden',
                backgroundColor: tint(aiColors.green, 0.15),
              }}
            >
              <View
                style={{
                  width: `${progress * 100}%`,
                  height: '100%',
                  backgroundColor: aiColors.green,
                }}
              />
            </View>
            <Typography.Text
              size={rpx(12)}
              color={reached ? aiColors.green : aiColors.mutedForeground}
            >
              {`本周 ${streak.weekPractices} / ${streak.weeklyGoal} 次`}
            </Typography.Text>
            {onEditGoal ? (
              <Pressable
                onPress={onEditGoal}
                accessibilityRole="button"
                accessibilityLabel="修改每周目标"
                hitSlop={8}
              >
                <Pencil size={rpx(14)} color={aiColors.mutedForeground} />
              </Pressable>
            ) : null}
          </View>
        </View>
        {onOpenLeaderboard ? (
          <Pressable
            onPress={onOpenLeaderboard}
            accessibilityRole="button"
            accessibilityLabel="本周练习榜"
            hitSlop={8}
            style={{ alignItems: 'center', gap: rpx(2) }}
          >
            <Trophy size={rpx(20)} color={aiColors.primary} />
            <Typography.Text size={rpx(11)} color={aiColors.primary}>
              练习榜
            </Typography.Text>
          </Pressable>
        ) : null}
      </View>
    );
  },
);
